import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Fade, Paper, useTheme } from "@mui/material";
import { BarDatum, BarLegendProps, BarTooltipProps, ResponsiveBar } from "@nivo/bar";
import { getColor, toPercent } from "./area-chart.component";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";

interface OwnProps {
  capacityObjects: { id: number | string; data: any; label: string | string[] }[];
  width?: string;
  height?: string;
}

type FloorReportData = {
  id: number | string;
  data: any;
  label: string | string[];
};

type Props = OwnProps;

const CapacityPerHourReport: FunctionComponent<Props> = props => {
  const { currentFloors, selectedZoneOrWorkplace } = useSelector(
    (state: RootState) => state.report
  );

  const { t } = useTranslation();
  const theme = useTheme();
  const [data, setData] = useState<{ id: number | string; data: any; label: string | string[] }[]>(
    []
  );

  const isComparing =
    currentFloors.length > 1 &&
    currentFloors[1].id &&
    selectedZoneOrWorkplace &&
    !selectedZoneOrWorkplace.open;

  const colors: any = isComparing
    ? { scheme: "category10" }
    : data.map(object =>
        getColor(
          object.data?.capacityPerformancePerHour?.reduce(
            (accu: any, currentValue: { capacityRate: any }) => accu + currentValue.capacityRate,
            0
          ) / 24
        )
      );

  const { newData, newKeyValues } = useMemo(() => {
    let newData: any[] = [];
    let newKeyValues: string[] = [];

    const firstFl = generateValues(data, currentFloors, 0);
    // no data exists
    if (!firstFl?.data?.capacityPerformancePerHour?.length) return { newData, newKeyValues };

    // Get dynamic keys from labels (default fallback provided)
    const firstKey = Array.isArray(firstFl.label)
      ? firstFl.label[0]
      : firstFl.label || "firstFloor";

    newData = firstFl.data.capacityPerformancePerHour.map(
      (val: { hour: number; capacityRate: number }) => ({
        name: val.hour,
        [firstKey]: Number(val.capacityRate.toFixed(2))
      })
    );
    newKeyValues = [firstKey];

    if (currentFloors.length > 1 && currentFloors[1].id) {
      const secondFl = generateValues(data, currentFloors, 1);
      // if no data for second floor exists, then return the first one only
      if (!secondFl) return { newData, newKeyValues };

      const secondKey = Array.isArray(secondFl.label)
        ? secondFl.label[0]
        : secondFl.label || "secondFloor";

      const rawData2 = secondFl.data.capacityPerformancePerHour;

      newData = newData.map((val: any) => {
        const match = rawData2.find((dat: { hour: number }) => dat.hour === val.name);
        return {
          ...val,
          [secondKey]: Number(match?.capacityRate.toFixed(2))
        };
      });
      newKeyValues = [firstKey, secondKey];
    }

    return { newData, newKeyValues };
  }, [data, currentFloors, selectedZoneOrWorkplace]);

  const legendsForFloorComparison = isComparing
    ? ([
        {
          dataFrom: "keys",
          anchor: "top",
          direction: "row",
          justify: false,
          translateX: 0,
          translateY: isComparing ? -30 : 0,
          itemWidth: 100,
          itemHeight: 20,
          itemsSpacing: 2,
          symbolSize: 15,
          itemDirection: "left-to-right"
        }
      ] as BarLegendProps[])
    : undefined;

  function generateToolTips(propsTooltip: React.PropsWithChildren<BarTooltipProps<BarDatum>>) {
    return (
      <strong
        style={{
          background: "#ffffff",
          color: "black",
          padding: 5,
          borderRadius: 4
        }}
      >
        {propsTooltip.indexValue + ":00 :" + " " + toPercent(Number(propsTooltip.value))}
      </strong>
    );
  }

  const getTickValues = () => {
    if (!newData) return { tickValues: 2 };
    const checkMaxValue = newData.every((value: { name: number; capaRate: number }) => {
      return value.capaRate < 0.01;
    });

    if (checkMaxValue) return { tickValues: 2 };
    return { tickValues: 3 };
  };

  useEffect(() => {
    setData(props.capacityObjects ?? []);
  }, [props.capacityObjects]);

  return (
    <Fade
      mountOnEnter
      unmountOnExit
      in={!!props.capacityObjects[0]?.data?.capacityPerformancePerHour}
    >
      <Paper
        data-testid="capacity-per-hour-report-paper"
        sx={{
          width: props.width ? props.width : "100%",
          height: props.height ? props.height : 380,
          marginTop: 2,
          marginBottom: 2
        }}
        variant={"outlined"}
      >
        <ResponsiveBar
          data={newData ?? []}
          keys={newKeyValues}
          indexBy="name"
          enableLabel={false}
          margin={{ top: isComparing ? 40 : 30, right: 30, bottom: 50, left: 60 }}
          padding={0.3}
          groupMode="grouped"
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          colors={colors}
          theme={{
            tooltip: { container: { background: "#ffffff", borderRadius: 4 } },
            grid: { line: { stroke: "#585858", strokeWidth: 1 } },
            axis: {
              ticks: { text: { fontSize: 10, fill: theme.palette.text.primary } },
              legend: { text: { fill: theme.palette.text.primary } }
            },
            text: { fill: theme.palette.text.primary }
          }}
          borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: t("capacity per hour"),
            legendOffset: 36,
            legendPosition: "middle",
            format: num => (num % 2 === 0 ? num : "") // display only the odd numbers at the bottom
          }}
          axisLeft={{
            tickValues: getTickValues().tickValues,
            format: tick => Number(tick) * 100 + "%"
          }}
          tooltip={generateToolTips}
          legends={legendsForFloorComparison}
        />
      </Paper>
    </Fade>
  );
};

export default CapacityPerHourReport;

export function generateValues(
  data: FloorReportData[],
  currentFloors: { id: number; open: boolean }[],
  index: number
) {
  if (currentFloors.length && data.some(d => d.id === currentFloors[index].id))
    return data.find(d => d.id === currentFloors[index].id);

  return data[index];
}
